import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { FormCheckbox, FormDatePicker, FormDropDownList, FormNumericTextBox, FormNumeroContrat, FormTextArea } from "commons/forms/form";
import { required, requiredDateGreater, requiredDateLower, requiredNumber } from "commons/forms/validators";
import FormFooterWithAccess from "commons/modal/FormFooterWithAccess";
import React, { useMemo, useState } from "react";
import * as dates from 'commons/models/dates';
import PropTypes from 'prop-types';
import { AbriAutobus, AffichageExterieur, Blogue, Circulaire, Infolettre, Journal, Numerique, Radio, SiteWeb, TV } from "pages/gant/constantes/medias";
import CustomModal from "commons/modal/CustomModal";
import LoaderContainer from "commons/container/LoaderContainer";
import { useAddCampagneReqMutation, useGetCampagneReqQuery, useGetDepartementsQuery, useGetDiffuseursQuery, useGetDivisionsQuery, useGetFormatsPageQuery, useGetMediasQuery, useGetThematiqueQuery } from "../../../../redux/api/cache";

export default function CreatePlanification(props) {
    const [divNum, setDivNum] = useState(-1);
    const [dateDebut, setDateDebut] = useState(null);
    const [dateFin, setDateFin] = useState(null);
    const [dateLimite, setDateLimite] = useState(null);
    const { data: divisions } = useGetDivisionsQuery();
    const { data: medias } = useGetMediasQuery();
    const { data: departements, isLoading } = useGetDepartementsQuery({ div: divNum }, { skip: divNum === -1 || divNum === 0 });
    const { data: diffuseurDropDown } = useGetDiffuseursQuery(props.media);
    const [diffCd, setDiffCd] = useState(props.addRequis ? props.data[0].diff_cd : "");
    const { data: thematiques } = useGetThematiqueQuery();
    const { data: formatsPage, isFetching: isFormatsPageFetching, isLoading: isFormatsPageLoading } = useGetFormatsPageQuery({ media: props.media, diff: diffCd }, { skip: !diffCd });
    const [addCampagneReq] = useAddCampagneReqMutation();
    const { data: campagneReqParent } = useGetCampagneReqQuery({ seq: props.project.camp_seq_parent, media: props.media }, { skip: props.project.camp_seq_parent === 0 });
    const isInfolettre = props.media === Infolettre;
    const isNumeric = props.media === Numerique;
    const isBlogue = props.media === Blogue;
    const isRadio = props.media === Radio;
    const isTV = props.media === TV;
    const isJournal = props.media === Journal;
    const isSiteWeb = props.media === SiteWeb;
    const isCirculaire = props.media === Circulaire;
    const isAbriAutobus = props.media === AbriAutobus;
    const isAffichageExterieur = props.media === AffichageExterieur;

    const handleSubmit = async (dataItem) => {
        let diffuseur = diffuseurDropDown?.data?.find(
            (item) => item.diff_nom === dataItem.diffuseur
        )?.diff_cd;
        let division = dataItem.division === "À définir" ? 0 : dataItem.division.split(' ')[0];
        let departement = dataItem.departement === "Tous" ? "" : dataItem.departement.split(' ')[0];
        let thematique = thematiques?.data?.find(
            (item) => item.them_descr === dataItem.thematique
        )?.them_cd;
        let media = medias?.data?.find((item) => item.media_cd === dataItem.media)
            .media_cd;
        let format = dataItem.FormatsPage ? formatsPage?.data?.find(f => f.mf_description === dataItem.FormatsPage)?.mf_format : "";
        let dDebut = dates.dateBdFormat(dataItem.dateDebut);
        let dLimite = dates.dateBdFormat(dataItem.dateLimite);
        let dFin = dates.dateBdFormat(dataItem.dateFin);

        await addCampagneReq({
            diff_cd: diffuseur,
            rid: props.selectedRow?.rid,
            camp_seq: props.camp_seq,
            media_cd: media,
            campr_date: dDebut,
            div_nom: dataItem.division,
            div_num: division,
            campr_nb_prod: dataItem.NbrProduit,
            campr_dt_limite: dLimite,
            campr_nb_annonce: dataItem.NbrAnnonces,
            campr_note: dataItem.noteAcheteur,
            campr_dt_fin: dFin,
            dept_cd: departement,
            them_cd: thematique,
            campr_ctr_no: dataItem.NumeroContrat,
            mf_format: format
        });

        props.setShowAddModal(false);
    };

    const dateDebutValidators = useMemo(() => {
        return [(e) => required(e, `Date de début`),
        (e) => requiredDateGreater(e, dateLimite, 'La date de début', 'la date limite'),
        (e) => requiredDateLower(e, dateFin, 'La date de début', 'la date de fin'),
        (e) => requiredDateGreater(e, dates.dateBdToDate(props.project.camp_dt_deb, '00:00'), 'La date de début', 'la date de début du projet'),
        (e) => requiredDateLower(e, dates.dateBdToDate(props.project.camp_dt_fin, '00:00'), 'La date de début', 'la date de fin du projet')
        ];
    }, [dateFin, dateLimite]);
    const dateFinValidators = useMemo(() => [(e) => required(e, 'Date fin'),
    (e) => requiredDateGreater(e, dateDebut, 'La date de fin', 'la date de début'),
    (e) => requiredDateGreater(e, dateLimite, 'La date de fin', 'la date limite'),
    (e) => requiredDateGreater(e, dates.dateBdToDate(props.project.camp_dt_deb, '00:00'), 'La date de fin', 'la date de début du projet'),
    (e) => requiredDateLower(e, dates.dateBdToDate(props.project.camp_dt_fin, '00:00'), 'La date de fin', 'la date de fin du projet')
    ], [dateDebut, dateLimite]);
    const dateLimiteValidators = useMemo(() => [(e) => required(e, 'Date limite'),
    (e) => requiredDateLower(e, dateDebut, 'La date limite', 'la date de début'),
    (e) => requiredDateLower(e, dateFin, 'La date limite', 'la date de fin')
    ], [dateDebut, dateFin]);
    const diffuseurValidator = (e) => required(e, 'Diffuseur');
    const thematiqueValidator = (e) => required(e, 'Thématique');
    const divisionValidator = (e) => required(e, 'Division');
    const nombreProduitsValidator = (e) => requiredNumber(e, 'Nombre de produits');
    const nombreAnnoncesValidator = (e) => requiredNumber(e, `Nombre d'annonces`);
    const numeroContratValidator = (e) => required(e, 'Numéro du contrat');
    const formatPageValidator = (e) => props.addRequis ? "" : required(e, 'Format de page');

    return <CustomModal title={props.addRequis ? "Ajout d'un requis" : "Ajout d'une planification"}
        width={"90vw"}
        closeIcon={true}
        onClose={() => props.setShowAddModal(false)}
        body={
            <>{((props.addRequis && (formatsPage || !diffCd)) || !props.addRequis) ?
                (<Form
                    initialValues={{
                        media: medias?.data?.find((item) => item.media_cd === props.media)
                            ?.media_cd,
                        camp_seq: props.camp_seq,
                        dateDebut:
                            props.addRequis
                                ? dates.dateBdToDate(props.data[0].campr_date, '00:00')
                                : null,
                        dateFin: props.addRequis
                            ? dates.dateBdToDate(props.data[0].campr_dt_fin, '00:00')
                            : null,
                        dateLimite: props.addRequis
                            ? dates.dateBdToDate(props.data[0].campr_dt_limite, '00:00')
                            : null,
                        diffuseur: props.addRequis ? diffuseurDropDown?.data?.find(
                            (item) => item.diff_cd === props.data[0].diff_cd
                        )?.diff_nom : null,
                        thematique: props.addRequis ? thematiques?.data?.find((item) => item.them_cd === props.data[0].them_cd)?.them_descr : null,
                        NbrAnnonces: 0,
                        FormatsPage: props.addRequis ? formatsPage?.data?.find((item) => item.mf_format === props.data[0].mf_format)?.mf_description : null
                    }}
                    onSubmit={handleSubmit}
                    render={(formRenderProps) => (
                        <FormElement>
                            <fieldset className={'k-form-fieldset'}>
                                <div className='inline'>
                                    <Field
                                        id={'dateDebut'}
                                        name={'dateDebut'}
                                        label={'Date de début'}
                                        disabled={props.addRequis}
                                        component={FormDatePicker}
                                        onChange={(e) => { setDateDebut(e.value); }}
                                        validator={dateDebutValidators}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            width: '200px',
                                            marginRight: '15px',
                                        }}
                                    />
                                    <Field
                                        id={'dateFin'}
                                        name={'dateFin'}
                                        label={'Date de fin'}
                                        disabled={props.addRequis}
                                        component={FormDatePicker}
                                        onChange={(e) => { setDateFin(e.value); }}
                                        validator={dateFinValidators}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            width: '200px',
                                            marginRight: '15px',
                                        }}
                                    />
                                    <Field
                                        id={'dateLimite'}
                                        name={'dateLimite'}
                                        label={'Date limite'}
                                        disabled={props.addRequis}
                                        component={FormDatePicker}
                                        onChange={(e) => { setDateLimite(e.value); }}
                                        validator={dateLimiteValidators}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            width: '200px',
                                            marginRight: '15px',
                                        }}
                                    />
                                    {(!isInfolettre && !isBlogue && !isSiteWeb) && <Field
                                        id={'Diffuseur'}
                                        name={'diffuseur'}
                                        label={'Diffuseur'}
                                        onChange={(e) => {
                                            formRenderProps.onChange("FormatsPage", {
                                                value: "",
                                            });
                                            setDiffCd(diffuseurDropDown?.data?.find(d => d.diff_nom === e.value)?.diff_cd);
                                        }}
                                        component={FormDropDownList}
                                        disabled={props.addRequis}
                                        data={diffuseurDropDown?.data?.filter(d => d.diff_statut).map((item) => item.diff_nom)}
                                        validator={diffuseurValidator}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            width: '300px',
                                            marginRight: '15px',
                                        }} />}
                                    {(isInfolettre || isBlogue || isSiteWeb) && <Field
                                        id={'Thematique'}
                                        name={'thematique'}
                                        label={'Thématique'}
                                        disabled={props.addRequis}
                                        component={FormDropDownList}
                                        data={thematiques?.data?.map((item) => item.them_descr)}
                                        validator={thematiqueValidator}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            width: '300px',
                                            marginRight: '15px',
                                        }} />}
                                    {(isAbriAutobus || isAffichageExterieur || isCirculaire || isJournal || isNumeric || isRadio || isTV) && (<Field
                                        id={'FormatsPage'}
                                        name={'FormatsPage'}
                                        disabled={props.addRequis || !diffCd || isFormatsPageFetching || isFormatsPageLoading || !formRenderProps.valueGetter('diffuseur')}
                                        label={`Formats de page`}
                                        format={'n0'}
                                        data={formatsPage?.data?.map(f => f.mf_description)}
                                        component={FormDropDownList}
                                        validator={formatPageValidator}
                                        wrapperStyle={{
                                            width: '300px',
                                            marginRight: '15px',
                                        }} />)}
                                    <Field
                                        id={'Division'}
                                        name={'division'}
                                        label={'Division'}
                                        onChange={(e) => {
                                            formRenderProps.onChange("departement", {
                                                value: "Tous",
                                            });
                                            setDivNum(e.value === "À définir" ? 0 : e.value.split(' ')[0]);
                                        }}
                                        component={FormDropDownList}
                                        data={divisions?.data?.map((item) => {
                                            if (item.div_num === 0)
                                                return "À définir";
                                            return `${item.div_num.toLocaleString('fr-CA', {
                                                minimumIntegerDigits: 2,
                                                useGrouping: false
                                            })} - ${item.div_nom}`;
                                        })}
                                        validator={divisionValidator}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            width: '300px',
                                            marginRight: '15px',
                                        }} />
                                    <Field
                                        id={'Departement'}
                                        name={'departement'}
                                        disabled={divNum === -1 || divNum === 0 || isLoading || !formRenderProps.valueGetter('division')}
                                        label={'Département'}
                                        defaultItem={'Tous'}
                                        component={FormDropDownList}
                                        data={departements?.data?.map((item) => `${item.dept_cd} - ${item.dept_nom}`)}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            width: '350px',
                                            marginRight: '15px',
                                        }} />
                                    <Field
                                        id={'NbrProduit'}
                                        name={'NbrProduit'}
                                        label={'Nb/Produit'}
                                        format={'n0'}
                                        min={0}
                                        component={FormNumericTextBox}
                                        validator={nombreProduitsValidator}
                                        wrapperStyle={{
                                            width: '100px',
                                            marginRight: '15px',
                                        }} />
                                    <Field
                                        id={'NbrAnnonces'}
                                        name={'NbrAnnonces'}
                                        label={`Nb/Pages`}
                                        format={'n0'}
                                        min={0}
                                        component={FormNumericTextBox}
                                        wrapperStyle={{
                                            width: '100px',
                                            marginRight: '15px',
                                        }}
                                        validator={nombreAnnoncesValidator} />
                                    {(!isBlogue && !isCirculaire && !isInfolettre && !isSiteWeb) && (<Field
                                        id={'NumeroContrat'}
                                        name={'NumeroContrat'}
                                        label={`Numéro du contrat`}
                                        format={'n0'}
                                        seqParent={props.project.camp_seq_parent}
                                        data={campagneReqParent?.data?.map(d => d.campr_ctr_no)}
                                        component={FormNumeroContrat}
                                        validator={numeroContratValidator}
                                        type="text"
                                        wrapperStyle={{
                                            width: '200px',
                                            marginRight: '15px',
                                        }} />)}
                                    <Field
                                        disabled
                                        id={'complete'}
                                        name={'complete'}
                                        label={`Complet`}
                                        component={FormCheckbox}
                                        wrapperStyle={{
                                            minWidth: '10rem',
                                            marginRight: '15px',
                                        }} />
                                </div>
                                <Field
                                    id={'noteAcheteur'}
                                    name={'noteAcheteur'}
                                    label={'Note acheteur'}
                                    optional={true}
                                    visited="false"
                                    rows={4}
                                    component={FormTextArea} />
                                <FormFooterWithAccess
                                    formRenderProps={formRenderProps} />
                            </fieldset>
                        </FormElement>
                    )}
                />) : <LoaderContainer />}

            </>}
    />;
}

CreatePlanification.propTypes = {
    data: PropTypes.any,
    create: PropTypes.func,
    media: PropTypes.any,
    remove: PropTypes.func,
    camp_seq: PropTypes.any,
    selectedRow: PropTypes.shape({
        campr_date: PropTypes.any,
        campr_dt_limite: PropTypes.any,
        campr_nb_annonce: PropTypes.any,
        campr_nb_prod: PropTypes.any,
        campr_note: PropTypes.any,
        campr_remarque: PropTypes.any,
        diff_cd: PropTypes.any,
        div_num: PropTypes.any,
        mf_format: PropTypes.any,
        rid: PropTypes.any,
    }),
    toggleDialog: PropTypes.func,
    setShowAddModal: PropTypes.any,
    project: PropTypes.any,
    addRequis: PropTypes.bool
};